import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getKakaoCodeFromURL,
  getNaverCodeFromURL,
} from "../services/socialAuth";
import styles from "./LoginPage.module.css";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [kakaoCallbackLoading, setKakaoCallbackLoading] = useState(false);
  const [naverCallbackLoading, setNaverCallbackLoading] = useState(false);
  const {
    signIn,
    signInWithKakao,
    handleKakaoLoginCallback,
    signInWithNaver,
    handleNaverLoginCallback,
  } = useAuth();
  const navigate = useNavigate();

  // 카카오/네이버 리다이렉트 콜백 처리
  useEffect(() => {
    const handleCallback = async () => {
      const { code: kakaoCode } = getKakaoCodeFromURL();
      const { code: naverCode } = getNaverCodeFromURL();

      // 카카오 콜백 처리
      if (kakaoCode) {
        setKakaoCallbackLoading(true);
        setSocialLoading("kakao");

        try {
          console.log("카카오 리다이렉트 콜백 처리 시작");

          const result = await handleKakaoLoginCallback();

          if (result.requiresSignup) {
            // 회원가입 필요 - 카카오 정보를 localStorage에 저장
            localStorage.setItem(
              "kakaoSignupInfo",
              JSON.stringify(result.kakaoInfo)
            );
            navigate("/register", {
              state: {
                fromKakao: true,
                kakaoInfo: result.kakaoInfo,
              },
            });
            return;
          }

          if (result && result.uid) {
            // 로그인 성공
            console.log("카카오 로그인 성공");
            navigate("/");
            return;
          }
        } catch (error: any) {
          console.error("카카오 로그인 오류:", error);
          setError("카카오 로그인에 실패했습니다. 다시 시도해주세요.");
          setKakaoCallbackLoading(false);
          setSocialLoading(null);
        }
      }

      // 네이버 콜백 처리
      if (naverCode) {
        setNaverCallbackLoading(true);
        setSocialLoading("naver");

        try {
          console.log("네이버 리다이렉트 콜백 처리 시작");

          const result = await handleNaverLoginCallback();

          if (result.requiresSignup) {
            // 회원가입 필요 - 네이버 정보를 localStorage에 저장
            localStorage.setItem(
              "naverSignupInfo",
              JSON.stringify(result.naverInfo)
            );
            navigate("/register", {
              state: {
                fromNaver: true,
                naverInfo: result.naverInfo,
              },
            });
            return;
          }

          if (result && result.uid) {
            // 로그인 성공
            console.log("네이버 로그인 성공");
            navigate("/");
            return;
          }
        } catch (error: any) {
          console.error("네이버 로그인 오류:", error);
          setError("네이버 로그인에 실패했습니다. 다시 시도해주세요.");
          setNaverCallbackLoading(false);
          setSocialLoading(null);
        }
      }
    };

    handleCallback();
  }, [handleKakaoLoginCallback, handleNaverLoginCallback, navigate]);

  // 카카오 또는 네이버 콜백 처리 중이면 로딩 화면만 표시
  if (kakaoCallbackLoading || naverCallbackLoading) {
    const provider = kakaoCallbackLoading ? "카카오" : "네이버";
    return (
      <div className={styles.loginPage}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>{provider} 로그인 처리 중...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("이메일과 비밀번호를 모두 입력해주세요.");
      return;
    }

    try {
      setError("");
      setLoading(true);
      await signIn(email, password);
      navigate("/");
    } catch (err: any) {
      let errorMessage = "로그인에 실패했습니다.";
      if (
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password"
      ) {
        errorMessage = "이메일 또는 비밀번호가 올바르지 않습니다.";
      } else if (err.code === "auth/too-many-requests") {
        errorMessage =
          "로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKakaoLogin = () => {
    setError("");
    setKakaoCallbackLoading(true);
    setSocialLoading("kakao");

    // 약간의 지연 후 리다이렉트 실행 (로딩 상태 표시를 위해)
    setTimeout(() => {
      signInWithKakao(); // 리다이렉트 실행
    }, 10);
  };

  const handleNaverLogin = () => {
    setError("");
    setNaverCallbackLoading(true);
    setSocialLoading("naver");

    // 약간의 지연 후 리다이렉트 실행 (로딩 상태 표시를 위해)
    setTimeout(() => {
      signInWithNaver(); // 리다이렉트 실행
    }, 10);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.loginPage}>
      {/* 헤더 */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <img src="/images/icons/arrow-left.svg" alt="뒤로가기" />
        </button>
        <h1>로그인</h1>
        <div></div>
      </div>

      <div className={styles.container}>
        {/* 로고 섹션 */}
        <div className={styles.logoSection}>
          <div className={styles.logoContainer}>
            {/* <img src="/images/icons/login-logo.svg" alt="펫밀리" /> */}
          </div>
          <h2>반가워요!</h2>
          <p>펫밀리에서 새로운 친구들을 만나보세요</p>
        </div>

        {/* 로그인 폼 */}
        <div className={styles.formSection}>
          {error && (
            <div className={styles.errorMessage}>
              <img src="/images/icons/error.svg" alt="오류" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.loginForm}>
            <div className={styles.inputGroup}>
              <label htmlFor="email">이메일</label>
              <div className={styles.inputWrapper}>
                <img src="/images/icons/email.svg" alt="이메일" />
                <input
                  type="email"
                  id="email"
                  placeholder="이메일을 입력해주세요"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">비밀번호</label>
              <div className={styles.inputWrapper}>
                <img src="/images/icons/password.svg" alt="비밀번호" />
                <input
                  type="password"
                  id="password"
                  placeholder="비밀번호를 입력해주세요"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={loading}
            >
              {loading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                "로그인"
              )}
            </button>
          </form>

          {/* 소셜 로그인 섹션 */}
          <div className={styles.socialSection}>
            <div className={styles.divider}>
              <span>또는</span>
            </div>

            <div className={styles.socialButtons}>
              <button
                className={`${styles.socialButton} ${styles.kakao}`}
                disabled={socialLoading === "kakao"}
                onClick={handleKakaoLogin}
              >
                {socialLoading === "kakao" ? (
                  <div className={styles.loadingSpinner}></div>
                ) : (
                  <>
                    <img src="/images/icons/kakao.svg" alt="카카오" />
                    <span>카카오로 로그인</span>
                  </>
                )}
              </button>
              <button
                className={`${styles.socialButton} ${styles.naver}`}
                disabled={socialLoading === "naver"}
                onClick={handleNaverLogin}
              >
                {socialLoading === "naver" ? (
                  <div className={styles.loadingSpinner}></div>
                ) : (
                  <>
                    <img src="/images/icons/naver.svg" alt="네이버" />
                    <span>네이버로 로그인</span>
                  </>
                )}
              </button>
            </div>

            <p className={styles.socialNotice}>소셜 로그인은 준비 중입니다</p>
          </div>
        </div>

        {/* 하단 링크 */}
        <div className={styles.bottomSection}>
          <div className={styles.linkContainer}>
            <span>아직 계정이 없으신가요?</span>
            <Link to="/register" className={styles.registerLink}>
              회원가입
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
